import { Button } from "@/components/ui/button";
import { ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

// Images - New AI Generated
import heroSlide1 from "@/assets/hero-slide-1.jpg";
import heroSlide2 from "@/assets/hero-slide-2.jpg";
import heroSlide3 from "@/assets/hero-slide-3.jpg";
import heroSlide4 from "@/assets/hero-slide-4.jpg";
import heroSlide5 from "@/assets/hero-slide-5.jpg";

interface Slide {
  title: string;
  subtitle: string;
  cta: string;
  ctaLink: string;
  image: string;
  gradient: string;
}

const slides: Slide[] = [
  {
    title: "EXPERTOS EN TABLEROS ELÉCTRICOS",
    subtitle: "Diseño, fabricación y montaje de tableros de distribución, fuerza y control. Cumplimiento normativa SEC NCh Elec. 4/2003 con protecciones termomagnéticas y diferenciales certificadas.",
    cta: "COTIZA TU PROYECTO",
    ctaLink: "#contact",
    image: heroSlide1,
    gradient: "from-primary/80 via-primary/70 to-dark-section/70"
  },
  {
    title: "INSTALACIONES ELÉCTRICAS CERTIFICADAS",
    subtitle: "Tendido de cables en bandeja portacables, canalización DLP, ductos EMT y sistemas de distribución en media y baja tensión con certificación TE1 y TE4.",
    cta: "VER SERVICIOS",
    ctaLink: "#services",
    image: heroSlide2,
    gradient: "from-emerald-900/80 via-dark-section/75 to-dark-section/70"
  },
  {
    title: "ILUMINACIÓN PROFESIONAL LED",
    subtitle: "Diseño luminotécnico con software DIALux, instalación de luminarias LED industriales, comerciales y de emergencia. Cálculo de luxes según normativa vigente.",
    cta: "VER PROYECTOS",
    ctaLink: "#projects",
    image: heroSlide3,
    gradient: "from-orange-800/80 via-red-900/75 to-dark-section/70"
  },
  {
    title: "CABLEADO ESTRUCTURADO",
    subtitle: "Instalación y certificación de redes de cobre Cat5e, Cat6, Cat6A y Cat7. Fibra óptica OM1, OM2, OM3, OM4 y OS1/OS2. Certificación Fluke con reportes OTDR y pruebas de canal permanente.",
    cta: "EXPLORAR SERVICIOS",
    ctaLink: "#services",
    image: heroSlide4,
    gradient: "from-violet-900/80 via-blue-900/75 to-dark-section/70"
  },
  {
    title: "SOLUCIONES DE NETWORKING",
    subtitle: "Configuración de switches gestionables, VLANs, routing y sistemas de red LAN/WAN. Infraestructura de telecomunicaciones con equipamiento de alta disponibilidad.",
    cta: "SOLICITAR COTIZACIÓN",
    ctaLink: "#contact",
    image: heroSlide5,
    gradient: "from-amber-700/80 via-orange-800/75 to-dark-section/70"
  }
];

const Hero = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true, 
      duration: 30,
      skipSnaps: false,
      dragFree: false 
    },
    [Autoplay({ 
      delay: 7000, 
      stopOnInteraction: false,
      stopOnMouseEnter: false,
      playOnInit: true
    })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setProgress(0);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    
    // Force autoplay to start
    const autoplay = emblaApi.plugins()?.autoplay;
    if (autoplay) {
      autoplay.play();
    }
    
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Progress bar animation
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 0;
        return prev + (100 / 70); // 7 seconds = 70 frames at 100ms
      });
    }, 100);

    return () => clearInterval(timer);
  }, [selectedIndex]);

  const handleCTAClick = (link: string) => {
    const element = document.querySelector(link);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-dark-foreground/10 z-30">
        <div
          className="h-full bg-primary transition-all duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Embla Viewport */}
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="embla__container flex h-full">
          {slides.map((slide, index) => (
            <div key={index} className="embla__slide flex-[0_0_100%] min-w-0 relative">
              {/* Background Image */}
              <div
                className="absolute inset-0 transition-opacity duration-700"
                style={{
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient}`} />
                <div
                  className="absolute inset-0"
                  style={{ background: "var(--gradient-mesh)" }}
                />
                
                {/* Animated Gradient Overlays */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {/* Gradient Layer 1 - Primary Color */}
                  <div 
                    className="absolute -top-1/2 -left-1/2 w-full h-full rounded-full blur-3xl opacity-30 animate-gradient-rotate"
                    style={{
                      background: `radial-gradient(circle, ${slide.gradient.includes('primary') ? 'hsl(var(--primary))' : 'rgb(239, 68, 68)'} 0%, transparent 70%)`
                    }}
                  />
                  
                  {/* Gradient Layer 2 - Secondary Color */}
                  <div 
                    className="absolute -bottom-1/2 -right-1/2 w-full h-full rounded-full blur-3xl opacity-25 animate-gradient-pulse"
                    style={{
                      background: `radial-gradient(circle, ${
                        slide.gradient.includes('emerald') ? 'rgb(16, 185, 129)' : 
                        slide.gradient.includes('violet') ? 'rgb(139, 92, 246)' : 
                        slide.gradient.includes('orange') ? 'rgb(251, 146, 60)' : 
                        'rgb(249, 115, 22)'
                      } 0%, transparent 70%)`,
                      animationDelay: '1s'
                    }}
                  />
                  
                  {/* Gradient Layer 3 - Mesh Pattern */}
                  <div 
                    className="absolute inset-0 opacity-20 animate-gradient-shift"
                    style={{
                      background: `linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.05) 25%, transparent 50%, rgba(255, 255, 255, 0.05) 75%, transparent 100%)`,
                      backgroundSize: '200% 200%'
                    }}
                  />
                  
                  {/* Animated Light Rays */}
                  <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-white/20 via-transparent to-transparent blur-sm animate-float" />
                  <div className="absolute top-0 right-1/3 w-1 h-full bg-gradient-to-b from-white/15 via-transparent to-transparent blur-sm animate-float" style={{ animationDelay: '2s' }} />
                </div>
              </div>

              {/* Content */}
              <div className="relative z-10 h-full flex items-center justify-center">
                <div className="container mx-auto px-4 text-center">
                  <div
                    className="animate-fade-in mb-8"
                    style={{
                      animationDelay: "0ms",
                      animationFillMode: "forwards"
                    }}
                  >
                    <h1 className="text-3xl md:text-5xl lg:text-6xl text-dark-foreground mb-6 font-montserrat font-bold tracking-tight drop-shadow-2xl">
                      {slide.title}
                    </h1>
                  </div>

                  <div
                    className="animate-fade-in opacity-0"
                    style={{
                      animationDelay: "200ms",
                      animationFillMode: "forwards"
                    }}
                  >
                    <p className="text-base md:text-lg lg:text-xl text-dark-foreground/90 max-w-4xl mx-auto mb-12 font-inter leading-relaxed drop-shadow-lg text-justify hyphens-auto" style={{ textAlignLast: 'center' }}>
                      {slide.subtitle}
                    </p>
                  </div>

                  <div
                    className="animate-scale-in opacity-0"
                    style={{
                      animationDelay: "400ms",
                      animationFillMode: "forwards"
                    }}
                  >
                    <Button
                      size="lg"
                      onClick={() => handleCTAClick(slide.ctaLink)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-7 text-lg font-semibold shadow-2xl hover:shadow-[0_0_40px_rgba(255,107,53,0.5)] hover:scale-105 transition-all duration-300 rounded-full group"
                    >
                      {slide.cta}
                      <ArrowDown className="ml-2 group-hover:translate-y-1 transition-transform" size={20} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <div className="container mx-auto h-full flex items-center justify-between px-4 lg:px-8">
          {/* Previous Button */}
          <button
            onClick={scrollPrev}
            className="pointer-events-auto glass hover:glass-dark p-4 rounded-full transition-all duration-300 hover:scale-110 group"
            aria-label="Previous slide"
          >
            <ChevronLeft className="text-dark-foreground group-hover:text-primary transition-colors" size={28} />
          </button>

          {/* Next Button */}
          <button
            onClick={scrollNext}
            className="pointer-events-auto glass hover:glass-dark p-4 rounded-full transition-all duration-300 hover:scale-110 group"
            aria-label="Next slide"
          >
            <ChevronRight className="text-dark-foreground group-hover:text-primary transition-colors" size={28} />
          </button>
        </div>
      </div>

      {/* Dot Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`transition-all duration-300 rounded-full ${
              index === selectedIndex
                ? "w-12 h-3 bg-primary shadow-[0_0_20px_rgba(255,107,53,0.6)]"
                : "w-3 h-3 bg-dark-foreground/40 hover:bg-dark-foreground/60 hover:scale-125"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float z-30">
        <div className="w-6 h-10 border-2 border-dark-foreground/40 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-dark-foreground/40 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;